import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 2.2 visual preview at /preview/lesson/2.2
 *
 * "Writing prompts that do not waste your time" — Week 2, lesson 2.
 *
 * Builds on 2.1 (tooling pre-check). Once you have the right tool, you have
 * to give it the right instructions. Teaches the 5-part prompt anatomy:
 * Role · Task · Context · Constraints · Output shape.
 *
 * Promotes "Context window" as the new vocab term with the full 4-block
 * treatment. 3 prompt rewrite drills where students pick the stronger
 * rewrite of a weak prompt.
 */

export default function LessonPreviewPage2_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 2 · Working with AI · Lesson 2.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Writing prompts that do not waste your time
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Judgment · Character</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 prompt rewrite drills</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Same model. Same tools. Two prompts. Two very different answers."
      accent="amber"
    >
      <p>
        A student has a history essay due tomorrow. The topic is the Cuban
        Missile Crisis. They sit down to use AI for help. Same model, same
        tools, same student. The only thing that changes is how they ask.
      </p>

      <PromptComparison />

      <p className="mt-4">
        The first answer is technically correct. It is also useless. Generic
        facts the student already half-knew, no structure to use, nothing they
        can drop into a draft. The second answer hands them an outline they can
        actually write from.
      </p>
      <p>
        The model did not get smarter in the second prompt. The student got
        clearer. The model can only work with what you give it. Vague in,
        vague out. Specific in, specific out.
      </p>
      <p className="text-text">
        2.1 was about picking the right tool. 2.2 is about giving that tool
        the right instructions. Five parts. Every prompt that matters.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time · Lesson 2.1"
      eyebrow="Three questions on the tooling pre-check. No looking back."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Name the three
          questions in the tooling pre-check, in order.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> Reasoning mode is
          the right call for one type of work and the wrong call for another.
          Name one example of each.
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> True or false: the
          best model with all tools on is always the right setup.
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        2.2 assumes the pre-check is automatic. If you have to think hard
        about any of those three, run 2.1 again before going further. The
        right prompt to the wrong model still gives you the wrong answer.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Teaching"
      eyebrow="The five parts every prompt that matters should have."
    >
      <p>
        Most bad AI answers are not the AI's fault. They are the prompt's
        fault. The good news is that there is a structure that fixes almost
        every weak prompt. It is not magic and it is not a hack. It is just
        the parts a smart person would include if they were writing
        instructions for someone else to follow.
      </p>

      <Subhead n="1" title="The five parts of a good prompt" />
      <p>
        Five labels, in this order. You do not have to include all five every
        time. You should know which one you are skipping and why.
      </p>

      <PromptAnatomy />

      <p>
        Three of those are the ones most people skip: Context, Constraints,
        and Output shape. The model can guess at them, but its guess is
        rarely what you would have asked for. Tell it.
      </p>

      <Subhead n="2" title="The model has a memory limit" />
      <p>
        Before you load a prompt up with context, you need to understand the
        space you are working in. Every model has a hard cap on how much it
        can hold in a single conversation. That cap has a name.
      </p>

      <VocabCard
        term="Context window"
        definition="The total amount of text a model can hold in working memory at one time: your prompt, any files you uploaded, the conversation history, and the response it is about to write. Measured in tokens, which are roughly pieces of words."
        analogy="The model's desk. Anything on the desk, it can see. Anything off the desk, it forgot. The desk has a fixed size. When new stuff arrives, old stuff falls off."
        hook="Context window is what the model can see right now. Training is what it learned long ago. Two different memories."
        matters="If your conversation gets long, or you paste in a huge document, older parts can fall out of the window. The model is not lying when it forgets what you said earlier in a long chat. It literally cannot see it anymore. Knowing the window exists changes how you structure long work."
      />

      <Subhead n="3" title="Show the model what good looks like" />
      <p>
        The fastest way to make a prompt stronger is to give the model an
        example of the output you want. Not a description of the output. The
        actual output, written out. One example beats a paragraph of
        adjectives.
      </p>

      <ExampleBlock />

      <p>
        This is called a <Vocab>few-shot example</Vocab>. The model uses your
        example to lock in tone, length, format, and level of detail. With one
        good example, a fast model can sometimes outperform a frontier model
        running on a vague prompt. Examples are leverage.
      </p>

      <Subhead n="4" title="The follow-up loop" />
      <p>
        Even strong prompts rarely land perfectly on the first try. That is
        normal. What separates fluent users from frustrated ones is what they
        do after the first response.
      </p>

      <FollowUpLoop />

      <p>
        Do not start a new conversation when a response is close but wrong.
        Stay in the same chat. The model can see what it just wrote. Tell it
        what to change. Three or four small adjustments almost always beat
        rewriting from scratch.
      </p>
    </Section>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn"
      eyebrow="Three weak prompts. Two rewrites each. Pick the stronger one."
    >
      <p>
        For each scenario you see a weak prompt and two rewrites. Pick the
        rewrite you would actually send. Then reveal the verdict and check
        your reasoning against the five-part structure.
      </p>

      <PromptRewrite
        n={1}
        scenario="You want AI to help you study for a biology test on cell division."
        weak="explain cell division"
        optionA={{
          label: 'Rewrite A',
          text: 'Explain cell division in detail. Cover mitosis, meiosis, and the phases of each. Include diagrams if possible.',
        }}
        optionB={{
          label: 'Rewrite B',
          text: 'You are a biology tutor for a 10th grader. I have a test on Friday on mitosis and meiosis. Explain the four phases of mitosis in plain language, then give me three questions I should be able to answer about each phase. Keep the whole thing under 400 words.',
        }}
        correct="B"
        verdict="Rewrite B. It hits all five parts: Role (biology tutor for a 10th grader), Task (explain the four phases, then give practice questions), Context (test on Friday, what topic), Constraints (plain language, under 400 words), Output shape (explanation then three questions per phase). Rewrite A is more specific than the weak version but still misses the role, the audience, the format you actually want, and any length limit. The model will give you a textbook page when you wanted a study guide."
      />

      <PromptRewrite
        n={2}
        scenario="You want AI to help you write a cover letter for a summer internship at a small architecture firm."
        weak="write me a cover letter for a job"
        optionA={{
          label: 'Rewrite A',
          text: 'Write me a one-page cover letter for a summer internship at Studio Bell Architects. I am a high school junior, I took two architecture electives, and I built a scale model of a public library that won a state competition. Tone: confident but not arrogant, like a 17-year-old who knows what they are doing. Open with the library project, not a generic "I am writing to apply."'
        }}
        optionB={{
          label: 'Rewrite B',
          text: 'Write a professional cover letter for an architecture internship. Make it sound mature and detailed. Mention my interest in the field and my willingness to learn.',
        }}
        correct="A"
        verdict="Rewrite A. The Context is loaded: specific firm, specific role, specific student details, a specific story to lead with. The Constraints are tight: one page, opening line specified, tone defined by a vivid description. The Output shape is implicit but clear. Rewrite B reads like a thousand other cover letters because it gave the model nothing distinctive to work with. The model can only be as specific as you are."
      />

      <PromptRewrite
        n={3}
        scenario="You want AI to help you brainstorm a topic for your senior project."
        weak="give me ideas for my senior project"
        optionA={{
          label: 'Rewrite A',
          text: 'I am a senior in high school. My senior project needs to combine something I am good at with something that helps my community. I am strong in coding and weak in public speaking. My town has a small library with no online catalog. Give me five project ideas that fit those constraints. For each: one sentence on what it is, one sentence on why it fits, one risk.',
        }}
        optionB={{
          label: 'Rewrite B',
          text: 'Give me five creative ideas for a senior project that uses technology in an interesting way. The project should be ambitious and impressive.',
        }}
        correct="A"
        verdict="Rewrite A. This is the part of prompting most students skip: telling the model what is true about you. Strong in coding, weak in public speaking, a specific real-world problem you know about. The Output shape (five ideas, three lines each, including a risk) means the response is actually usable. Rewrite B will get you a list of impressive-sounding generic projects that have nothing to do with you. The personal context is what makes the output yours."
      />
    </Section>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const items = [
    {
      q: 'Name the five parts of a good prompt, in order.',
      a: 'Role, Task, Context, Constraints, Output shape. Role tells the model who it is being for you. Task tells it what to do. Context tells it what is true about your situation. Constraints set the rules: length, tone, what to avoid. Output shape tells it what the response should look like.',
    },
    {
      q: 'Which of the five parts do most weak prompts skip?',
      a: 'Context, Constraints, and Output shape. Most weak prompts give the Task and stop. The model then has to guess at the other three, and its guess is rarely what you would have asked for. Specifying those three is the biggest leverage in prompting.',
    },
    {
      q: 'What is a context window, and why does it matter?',
      a: 'The context window is the model\'s working memory in a single conversation: your prompt, any files, the chat history, and the response it is about to write. It is a fixed size. When new content arrives, old content falls off. In long chats or with big documents, parts of what you said earlier can disappear from view. Knowing this changes how you structure long work.',
    },
    {
      q: 'You ask an AI to write something and the result is close but the tone is wrong. What is your move?',
      a: 'Stay in the same chat. Do not start over. Tell the model what to change about the tone, ideally with an example of the tone you want. Three or four small adjustments in the same thread almost always beat a new prompt. The model can see what it just wrote and what you did not like about it.',
    },
    {
      q: 'A friend says "I just give the AI more words and it works better." Where is that half-right and half-wrong?',
      a: 'Half-right: more specific information almost always helps. Context, constraints, and examples make a real difference. Half-wrong: more words alone do nothing. A long, vague prompt is just as weak as a short, vague prompt. Quality of the information matters, not the count. Five clear sentences beat fifty vague ones.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Use it as a checklist for every prompt that matters."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 2.2 · Lock card · Five-part prompt
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          The five parts. Use them in this order. Skip any on purpose, not by
          accident.
        </p>

        <div className="mt-6 space-y-3">
          <PartTile
            n={1}
            label="Role"
            q="Who is the AI being for you?"
            sub="Tutor. Editor. Coach. Skeptical reviewer. The role shapes voice, tone, and what the model treats as relevant."
          />
          <PartTile
            n={2}
            label="Task"
            q="What do you want it to do?"
            sub="One verb, one clear outcome. Explain, draft, compare, critique, brainstorm. If you have two tasks, do them as two prompts."
          />
          <PartTile
            n={3}
            label="Context"
            q="What is true about your situation?"
            sub="Audience, deadline, what you already tried, what you are good or bad at. The model can only be as specific as you are."
          />
          <PartTile
            n={4}
            label="Constraints"
            q="What are the rules?"
            sub="Length, tone, format, what to avoid, what to assume. Constraints make the output usable, not generic."
          />
          <PartTile
            n={5}
            label="Output shape"
            q="What should the response look like?"
            sub="A list. A table. A draft with three sections. One paragraph then three questions. Tell the model the shape, and show an example when you can."
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The principle behind the five parts
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            The model can only be as specific as you are. Vague in, vague
            out. Specific in, specific out. The five-part structure is just
            the shape of being specific on purpose.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Pre-check the tool. Write the five parts. Send. Then follow up.
        </p>
      </div>
    </Section>
  );
}

// ---------- Custom components for 2.2 ----------

function PromptComparison() {
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-5">
        <div className="flex flex-col gap-1.5">
          <span className="inline-block w-fit shrink-0 rounded-md bg-rose-500/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-rose-300">
            Prompt 1
          </span>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            What the student typed
          </p>
        </div>
        <p className="mt-3 rounded-md border border-line bg-ink/60 px-3 py-2 font-mono text-xs text-text/90">
          help me with my essay on the cuban missile crisis
        </p>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted">
          What came back
        </p>
        <p className="mt-2 text-sm text-text">
          The Cuban Missile Crisis was a 13-day confrontation in October 1962
          between the United States and the Soviet Union over Soviet ballistic
          missiles in Cuba. It is widely considered the closest the Cold War
          came to nuclear war. Key figures included President Kennedy, Premier
          Khrushchev, and Fidel Castro...
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-rose-300">
          Generic textbook page. Unusable as a draft.
        </p>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
        <div className="flex flex-col gap-1.5">
          <span className="inline-block w-fit shrink-0 rounded-md bg-emerald-500/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
            Prompt 2
          </span>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            What the student typed
          </p>
        </div>
        <p className="mt-3 rounded-md border border-line bg-ink/60 px-3 py-2 font-mono text-xs text-text/90">
          You are a history teacher. I am a 10th grader writing a 5-paragraph
          essay due tomorrow on whether Kennedy's blockade was the right call
          during the Cuban Missile Crisis. Give me three possible thesis
          statements, each with the strongest counter-argument I would need to
          address. Keep each thesis under 30 words.
        </p>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted">
          What came back
        </p>
        <p className="mt-2 text-sm text-text">
          Thesis A (defends the blockade): Kennedy's naval quarantine was the
          right call because it...
          <br />
          <span className="text-muted">
            Counter to address: Critics argue diplomatic backchannels could
            have...
          </span>
          <br />
          Thesis B (critiques the blockade): Kennedy's blockade unnecessarily
          escalated...
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          Three thesis options, ready to write from.
        </p>
      </div>
    </div>
  );
}

function PromptAnatomy() {
  const parts = [
    {
      n: '1',
      label: 'Role',
      q: 'Who is the AI being for you?',
      example: '"You are a biology tutor for a 10th grader."',
    },
    {
      n: '2',
      label: 'Task',
      q: 'What do you want it to do?',
      example: '"Explain the four phases of mitosis."',
    },
    {
      n: '3',
      label: 'Context',
      q: 'What is true about your situation?',
      example: '"I have a test on Friday. I keep mixing up prophase and metaphase."',
    },
    {
      n: '4',
      label: 'Constraints',
      q: 'What are the rules?',
      example: '"Plain language. Under 400 words. No textbook jargon."',
    },
    {
      n: '5',
      label: 'Output shape',
      q: 'What should the response look like?',
      example: '"Explanation, then three practice questions per phase."',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Five parts · In this order · Skip on purpose, not by accident
        </p>
      </div>
      <div className="divide-y divide-line">
        {parts.map((p) => (
          <div
            key={p.n}
            className="grid gap-3 px-5 py-4 md:grid-cols-[40px_140px_1fr]"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber font-mono text-sm font-semibold text-ink">
              {p.n}
            </span>
            <div>
              <p className="font-mono text-sm uppercase tracking-widest text-amber">
                {p.label}
              </p>
              <p className="mt-1 text-xs text-muted">{p.q}</p>
            </div>
            <p className="font-mono text-xs text-text/90 md:pt-1">
              {p.example}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExampleBlock() {
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Few-shot example · The pattern that quietly upgrades any prompt
        </p>
      </div>
      <div className="grid gap-0 md:grid-cols-2 md:divide-x md:divide-line">
        <div className="px-5 py-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Without an example
          </p>
          <p className="mt-2 rounded-md border border-line bg-ink/60 px-3 py-2 font-mono text-xs text-text/90">
            Write me three book recommendations in a friendly tone.
          </p>
          <p className="mt-3 text-sm text-text/80">
            You get three book recommendations. The tone is generic friendly.
            Length varies. Format varies. Might be a paragraph, might be
            bullets.
          </p>
        </div>
        <div className="px-5 py-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
            With an example
          </p>
          <p className="mt-2 rounded-md border border-amber/40 bg-amber/[0.04] px-3 py-2 font-mono text-xs text-text/90">
            Write me three book recommendations in this format:
            <br />
            <br />
            <span className="text-amber">Title.</span> One sentence on what
            it is about. One sentence on why you would like it.
            <br />
            <br />
            Like this:
            <br />
            <span className="text-amber">Project Hail Mary.</span> A
            scientist wakes up alone on a spaceship with no memory of how he
            got there. You like it because the puzzles feel earned and the
            friendship at the center is unexpected.
          </p>
          <p className="mt-3 text-sm text-text/80">
            Three recommendations, all in the exact format you showed. Length
            locked. Voice locked. Done.
          </p>
        </div>
      </div>
    </div>
  );
}

function FollowUpLoop() {
  const steps = [
    {
      n: '1',
      label: 'Send the first prompt',
      body: 'Use the five-part structure. Get the first response.',
    },
    {
      n: '2',
      label: 'Read with the audit in mind',
      body: 'Run the four checks from 1.4 quickly. What is wrong, missing, or off-tone?',
    },
    {
      n: '3',
      label: 'Reply with what to change',
      body: 'Stay in the same chat. Be specific. "Shorter. Drop the second paragraph. Make the tone less corporate."',
    },
    {
      n: '4',
      label: 'Repeat until it lands',
      body: 'Three or four passes is normal. If you are past five, your original prompt was probably missing something big. Go back to the five-part structure.',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Follow-up loop · One prompt rarely lands · Same chat, small fixes
        </p>
      </div>
      <div className="divide-y divide-line">
        {steps.map((s) => (
          <div
            key={s.n}
            className="grid gap-3 px-5 py-4 md:grid-cols-[40px_180px_1fr]"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber/20 font-mono text-sm font-semibold text-amber">
              {s.n}
            </span>
            <p className="font-mono text-sm uppercase tracking-widest text-amber">
              {s.label}
            </p>
            <p className="text-sm text-text/90 md:pt-1">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromptRewrite({
  n,
  scenario,
  weak,
  optionA,
  optionB,
  correct,
  verdict,
}: {
  n: number;
  scenario: string;
  weak: string;
  optionA: { label: string; text: string };
  optionB: { label: string; text: string };
  correct: 'A' | 'B';
  verdict: string;
}) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const isCorrect = picked === correct;
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Drill {n} · {scenario}
        </p>
      </div>
      <div className="px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          The weak prompt
        </p>
        <p className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/[0.04] px-3 py-2 font-mono text-xs text-text/90">
          {weak}
        </p>

        <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-muted">
          Pick the stronger rewrite
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <RewriteOption
            label={optionA.label}
            text={optionA.text}
            selected={picked === 'A'}
            verdict={picked ? (correct === 'A' ? 'right' : 'wrong') : null}
            onClick={() => setPicked('A')}
          />
          <RewriteOption
            label={optionB.label}
            text={optionB.text}
            selected={picked === 'B'}
            verdict={picked ? (correct === 'B' ? 'right' : 'wrong') : null}
            onClick={() => setPicked('B')}
          />
        </div>

        {picked && (
          <div
            className={`mt-5 rounded-md border px-4 py-3 ${
              isCorrect
                ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                : 'border-amber/40 bg-amber/[0.04]'
            }`}
          >
            <p
              className={`font-mono text-[10px] uppercase tracking-widest ${
                isCorrect ? 'text-emerald-300' : 'text-amber'
              }`}
            >
              {isCorrect ? 'Correct' : 'Not quite'} · Model verdict
            </p>
            <p className="mt-1.5 text-sm text-text/90">{verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function RewriteOption({
  label,
  text,
  selected,
  verdict,
  onClick,
}: {
  label: string;
  text: string;
  selected: boolean;
  verdict: 'right' | 'wrong' | null;
  onClick: () => void;
}) {
  const borderClass =
    verdict === 'right' && selected
      ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
      : verdict === 'wrong' && selected
      ? 'border-rose-500/50 bg-rose-500/[0.05]'
      : selected
      ? 'border-amber bg-amber/[0.06]'
      : 'border-line bg-ink/40 hover:border-amber/50';
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-colors ${borderClass}`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          selected ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-3 text-sm text-text/90">{text}</p>
    </button>
  );
}

function PartTile({
  n,
  label,
  q,
  sub,
}: {
  n: number;
  label: string;
  q: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber font-mono text-sm font-semibold text-ink">
          {n}
        </span>
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {label}
        </p>
      </div>
      <p className="mt-3 text-base font-semibold text-text">{q}</p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">{sub}</p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 2.3
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            When to trust, when to verify, when to walk away
          </h3>
          <p className="mt-2 text-sm text-muted">
            You picked the right tool. You wrote a strong prompt. The answer
            looks good. Now what? 2.3 builds the trust ladder: the three
            categories of AI output and what to do with each.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Vocab({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dotted border-amber/60 text-text">
      {children}
    </span>
  );
}

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 2.2 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: '2.1 review' },
    { id: 'sec-teaching', label: 'Five parts' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
